import { Controller } from 'stimulus'
import { useDebounce } from 'stimulus-use'

export default class extends Controller {
  static targets = ['slide', 'prevButton', 'nextButton', 'progress', 'footer']
  static debounces = ['validateCurrentSlide']

  connect() {
    this.currentSlideIndex = null
    this.revealSlide(0)
    useDebounce(this, 'validateCurrentSlide')

    this.isTransitioning = false
  }

  revealSlide(index) {
    this.currentSlideIndex = index

    this.visibleSlides.forEach((slide) => slide.classList.add('hidden'))
    this.visibleSlides[index].classList.remove('hidden')

    this.footerTarget.classList.toggle('hidden', index == 0)

    this.currentSlide.querySelector('input, textarea')?.focus()

    this.toggleButtons()
    this.updateProgress()
    this.isTransitioning = false
  }

  nextSlide() {
    if (this.isTransitioning) return
    this.isTransitioning = true

    // Prevent going to the next slide unless current field is valid
    // Otherwise, when we get to the last slide we won't be able to submit
    if (!this.currentSlideValid()) {
      this.isTransitioning = false
      return
    }

    const nextSlideIndex = this.currentSlideIndex + 1

    if (nextSlideIndex >= this.visibleSlides.length) {
      this.isTransitioning = false
      return
    }

    this.revealSlide(nextSlideIndex)
  }

  prevSlide() {
    if (this.isTransitioning) return
    this.isTransitioning = true

    const prevSlideIndex = this.currentSlideIndex - 1

    if (prevSlideIndex < 0) return

    this.revealSlide(prevSlideIndex)
  }

  validateCurrentSlide(event) {
    setTimeout(() => {
      this.toggleButtons()

      // Auto-advance for choice fields (radio, select, yes/no)
      if (event && this.shouldAutoAdvance(event.target)) {
        setTimeout(() => this.nextSlide(), 100)
      }
    }, 100)
  }

  shouldAutoAdvance(target) {
    if (target.type === 'radio' || target.tagName === 'SELECT') return true

    if (target.type === 'checkbox' && target.closest('.yes-no-component'))
      return true

    return false
  }

  handleEnter(event) {
    // Allow Enter in textareas for line breaks
    if (event.target.tagName === 'TEXTAREA') return

    event.preventDefault()

    const isLastSlide = this.currentSlideIndex == this.visibleSlides.length - 1

    if (isLastSlide) {
      this.element.closest('form').requestSubmit()
    } else {
      this.nextSlide()
    }
  }

  toggleButtons() {
    this.prevButtonTargets.forEach((button) => {
      button.classList.toggle('invisible', this.currentSlideIndex == 0)
    })

    const isLastSlide = this.currentSlideIndex == this.visibleSlides.length - 1
    const isValid = this.currentSlideValid()

    this.nextButtonTargets.forEach((button) => {
      button.classList.toggle('invisible', isLastSlide)
      button.disabled = !isValid
      button.classList.toggle('opacity-50', !isValid)
      button.classList.toggle('cursor-not-allowed', !isValid)
    })
  }

  updateProgress() {
    // Do not count the Welcome slide in the progress
    this.progressTarget.innerHTML = `${this.currentSlideIndex} / ${
      this.visibleSlides.length - 1
    }`
  }

  currentSlideValid() {
    if (!this.currentSlide) return true

    const inputs = this.currentSlide.querySelectorAll('input, select, textarea')
    const nativelyValid = Array.from(inputs).every((input) =>
      input.checkValidity()
    )

    return nativelyValid && this.requiredYesNoFieldsAnswered()
  }

  // Yes/No fields can't use a native `required` attribute (it would force Yes to be checked,
  // rejecting a legit No — ENG-8846), so checkValidity() alone can't see whether a required
  // one is still unanswered. Check the answered state directly (ENG-32451).
  requiredYesNoFieldsAnswered() {
    const fields = this.currentSlide.querySelectorAll(
      '.yes-no-component[data-yes-no-required-value="true"]'
    )

    return Array.from(fields).every((field) => {
      const checkbox = field.querySelector('input[type="checkbox"]')
      const hidden = field.querySelector('input[type="hidden"]')
      return checkbox.checked || !hidden.disabled
    })
  }

  get visibleSlides() {
    return this.slideTargets.filter(
      (slide) =>
        slide.querySelector('.js-survey-field-conditionally-hidden') === null
    )
  }

  get currentSlide() {
    return this.visibleSlides[this.currentSlideIndex]
  }
}
